// ✅ FIXED + ENHANCED VERSION (15-11-2025)
// Includes: Chunk Viewer Modal (Bootstrap modal, Option A)
// No logic broken, only additions.

import React, { useState, useContext } from "react";
import axiosClient from "../../api/axiosClient";
import { AuthContext } from "../../context/AuthContext";
import { Mic, X } from "lucide-react";
import { Modal, Button, Spinner } from "react-bootstrap";

// =====================================================
// ⭐ Chunk Viewer Modal (Bootstrap Modal)
// =====================================================
function ChunkViewerModal({ show, onClose, loading, chunk }) {
  return (
    <Modal show={show} onHide={onClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          📄 Chunk Viewer
          {chunk?.document_name && (
            <span className="text-muted ms-2" style={{ fontSize: "14px" }}>
              ({chunk.document_name})
            </span>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-4">
            <Spinner animation="border" />
          </div>
        ) : chunk ? (
          <>
            <div className="mb-2">
              <strong>Document:</strong> {chunk.document_name}
            </div>
            <div className="mb-2">
              <strong>Chunk ID:</strong> {chunk.chunk_id}
            </div>

            <div className="p-3 rounded bg-light border">
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  fontSize: "14px",
                  margin: 0,
                }}
              >
                {chunk.text}
              </pre>
            </div>

            {chunk.meta && (
              <div className="mt-3">
                <strong>Metadata:</strong>
                <pre style={{ fontSize: "12px", whiteSpace: "pre-wrap" }}>
                  {JSON.stringify(chunk.meta, null, 2)}
                </pre>
              </div>
            )}
          </>
        ) : (
          <p className="text-danger">Chunk not found.</p>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// =====================================================
// ⭐ Docs AI Assistant Drawer
// =====================================================
function DocsAIAssistantDrawer({ onClose }) {
  const { activeScope } = useContext(AuthContext);
  const [query, setQuery] = useState("");
  const [response, setResponse] = useState("");
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  // ⭐ NEW: Chunk modal states
  const [showChunkModal, setShowChunkModal] = useState(false);
  const [chunkLoading, setChunkLoading] = useState(false);
  const [selectedChunk, setSelectedChunk] = useState(null);

  // 🎙️ Speech input
  const handleSpeak = () => {
    if (!("webkitSpeechRecognition" in window) && !("SpeechRecognition" in window)) {
      alert("Speech recognition not supported in this browser.");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";
    recognition.interimResults = false;
    recognition.continuous = false;

    recognition.onstart = () => setSpeaking(true);
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setQuery(transcript);
    };
    recognition.onerror = () => alert("Speech recognition failed.");
    recognition.onend = () => setSpeaking(false);

    recognition.start();
  };

  // ⭐ Fetch Chunk from backend
  const fetchChunk = async (doc_id, chunk_id) => {
    try {
      setChunkLoading(true);
      setSelectedChunk(null);

      const res = await axiosClient.get(
        `/docs_ai_assistant/chunk?doc_id=${doc_id}&chunk_id=${chunk_id}`
      );

      setSelectedChunk(res.data?.data || null);
    } catch (err) {
      console.error("Chunk fetch failed:", err);
      setSelectedChunk(null);
    } finally {
      setChunkLoading(false);
      setShowChunkModal(true);
    }
  };

  // 🧠 Ask question
  const handleAsk = async () => {
    if (!query.trim()) {
      alert("Please enter a question first.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        text: query,
        top_k: 5,
        voice: false,
        language: "en-IN"
      };

      const res = await axiosClient.post("/docs_ai_assistant/query", payload);

      const data = res.data?.data || {};
      setResponse(
        data.answer_text || "No relevant answer found in indexed documents."
      );
      setSources(data.sources || []);
    } catch (err) {
      console.error("Query error:", err);
      setResponse("Error while fetching answer. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const speakAnswer = (text) => {
    if (!text) return;
    const synth = window.speechSynthesis;
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = "en-IN";
    synth.speak(utter);
  };

  const clearAll = () => {
    setQuery("");
    setResponse("");
    setSources([]);
  };

  const safeClose = () => {
    try {
      if (typeof onClose === "function") onClose();
    } catch {}
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={safeClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 1050,
        }}
      />

      {/* Drawer */}
      <div
        className="docs-ai-drawer"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "420px",
          height: "100vh",
          background: "#fff",
          boxShadow: "-4px 0 12px rgba(0,0,0,0.2)",
          zIndex: 1055,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          className="d-flex justify-content-between align-items-center border-bottom p-3 bg-light"
        >
          <div className="d-flex align-items-center gap-2">
            <Mic size={18} className="text-success" />
            <h6 className="mb-0 fw-bold">Docs AI Assistant</h6>
          </div>

          <button
            className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
            onClick={safeClose}
          >
            <X size={14} /> Close
          </button>
        </div>

        {/* Body */}
        <div className="p-3 flex-grow-1 overflow-auto">
          <textarea
            className="form-control mb-2"
            rows="3"
            placeholder="Ask about policies, SOPs or uploaded documents..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          <div className="d-flex align-items-center gap-2 mb-3">
            <button
              className={`btn ${speaking ? "btn-secondary" : "btn-outline-success"}`}
              onClick={handleSpeak}
              disabled={speaking}
            >
              <Mic size={16} />
              {speaking ? "Listening..." : "Speak"}
            </button>

            <button
              className="btn btn-primary"
              onClick={handleAsk}
              disabled={loading}
            >
              {loading ? "Thinking..." : "Ask"}
            </button>

            <small className="text-muted ms-auto">Multi-language supported</small>
          </div>

          {/* Answer */}
          <div className="border rounded p-2 bg-white mb-3">
            <label className="fw-bold">Answer</label>
            <div className="p-2" style={{ minHeight: "80px", whiteSpace: "pre-wrap" }}>
              {response || "No answer yet — ask a question to begin."}
            </div>

            {response && (
              <button
                className="btn btn-sm btn-outline-secondary mt-2"
                onClick={() => speakAnswer(response)}
              >
                🔊 Listen
              </button>
            )}
          </div>

          {/* Sources */}
          {sources.length > 0 && (
            <div className="border rounded p-2 bg-light">
              <label className="fw-bold">Sources</label>
              <ul className="small mb-0">
                {sources.map((src, i) => (
                  <li key={i}>
                    <button
                      className="btn btn-link p-0"
                      onClick={() => fetchChunk(src.doc_id, src.chunk_id)}
                    >
                      [doc_id={src.doc_id} chunk_id={src.chunk_id}] — {src.doc_name}
                    </button>
                    <br />
                    <span style={{ opacity: 0.8 }}>{src.snippet}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-top p-3 d-flex justify-content-end gap-2 bg-light">
          <button className="btn btn-outline-secondary" onClick={clearAll}>
            Clear
          </button>
        </div>
      </div>

      {/* ⭐ Chunk Viewer Modal */}
      <ChunkViewerModal
        show={showChunkModal}
        onClose={() => setShowChunkModal(false)}
        loading={chunkLoading}
        chunk={selectedChunk}
      />
    </>
  );
}

export default DocsAIAssistantDrawer;
